import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    queueMicrotask(() => this.#updateZoomButton());
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by morphing the current media gallery in place.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    morph(this, newMediaGallery, {
      ...MORPH_OPTIONS,
      childrenOnly: false,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;

        return node.getAttribute('slide-id') || node.dataset.mediaId || node.id;
      },
    });
    this.updatedCallback();

    const selectedMediaId = event.detail.resource?.featured_media?.id;
    if (selectedMediaId) {
      queueMicrotask(() => {
        this.refs.slideshow?.select({ id: `${selectedMediaId}` }, undefined, { animate: false });
      });
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Keeps the gallery-level zoom button in sync with the selected slide.
   * @param {import('./events').SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    this.#updateZoomButton(event.detail.slide);
  };

  /**
   * Changes the desktop carousel slide when an arrow key is pressed inside the gallery.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (
      !mediaQueryLarge.matches ||
      this.presentation !== 'carousel' ||
      event.defaultPrevented ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey
    ) {
      return;
    }

    if (event.target instanceof Element && event.target.closest('dialog[open]')) return;

    if (event.key === 'ArrowLeft') {
      this.slideshow?.previous(event);
    } else if (event.key === 'ArrowRight') {
      this.slideshow?.next(event);
    }
  };

  /**
   * Shows the fixed zoom button only when the active media is an image.
   * @param {HTMLElement} [slide]
   */
  #updateZoomButton(slide) {
    const zoomButton = this.querySelector('.product-media-gallery__mobile-zoom-button');
    if (!(zoomButton instanceof HTMLButtonElement)) return;

    const activeSlide = slide ?? this.slideshow?.slides?.[this.slideshow.current];
    zoomButton.hidden = !activeSlide?.classList.contains('product-media-container--image');

    const mediaId = activeSlide?.getAttribute('slide-id');
    const activeIndex =
      this.refs.zoomDialogComponent?.refs.media.findIndex((media) => media.dataset.mediaId === mediaId) ?? 0;
    const labelTemplate = zoomButton.dataset.labelTemplate;
    if (labelTemplate) zoomButton.setAttribute('aria-label', labelTemplate.replace('__INDEX__', `${activeIndex + 1}`));
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Zooms the image currently selected in the slideshow.
   * @param {PointerEvent} event - The pointer event.
   */
  zoomCurrent(event) {
    const slide = this.slideshow?.slides?.[this.slideshow.current];
    if (!slide?.classList.contains('product-media-container--image')) return;

    const mediaId = slide.getAttribute('slide-id');
    const index =
      this.refs.zoomDialogComponent?.refs.media.findIndex((media) => media.dataset.mediaId === mediaId) ?? -1;
    if (index < 0) return;

    this.zoom(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
